<template>
  <main class="flex flex-row-reverse">
    <div
      v-style:top="{
        sm: pxToRem(header.sticky.offset),
        lg: pxToRem(header.sticky.offset),
      }"
      class="sticky right-0 top-0 h-screen flex shrink-0 center overflow-hidden bg-black text-center text-xs c-white fw-bold font-secondary md:text-sm"
    >
      <nav
        ref="navRef"
        :aria-label="hero.title"
        class="max-h-full overflow-auto scrollbar-none"
        style="width: 3.125rem"
      >
        <ul class="f-col py-20" style="gap: 0.5rem">
          <li v-for="({ heading, id }, i) in years" :key="i" ref="listItemRefs">
            <base-link
              :aria-current="id === currentSectionId ? 'location' : undefined"
              class="relative h-5 flex center"
              :to="`#${id}`"
              @click="scrollToElement(id, { offset: 0 })"
            >
              <span
                class="absolute left-0"
                :class="{ 'op-0': id !== currentSectionId }"
                style="
                  border-top: 5px solid transparent;
                  border-bottom: 5px solid transparent;
                  border-left: 5px solid currentColor;
                  top: calc(50% - 5px)
                "
              />
              <template v-if="(i + 1) % 3 === 0 || i === 0 || i === years.length - 1">
                {{ heading }}
              </template>
              <template v-else>
                <span class="sr-only">{{ heading }}</span>
                <span class="block h-1 w-1 rounded-full" style="background: currentColor" />
              </template>
            </base-link>
          </li>
        </ul>
      </nav>
    </div>
    <div v-style:bgi="bg" class="grow" style="background-attachment: fixed">
      <div
        v-style:bgi="hero.bg"
        class="pb-4 pt-16 text-center lg:pb-8 lg:pt-24"
        style="background-size: cover"
      >
        <h1 class="ws-pre-wrap title-1 uppercase lg:text-6xl">
          {{ hero.title }}
        </h1>
        <base-picture v-bind="hero.logo" />
      </div>
      <section
        v-for="({ blocks, heading, id }, i) in years"
        :key="i"
        ref="sectionsRefs"
        :id
        class="pt-12 container lg:pt-20"
        :class="{ 'pb-12': i === years.length - 1 }"
        :data-scroll-el="id"
      >
        <h2 :id="`${id}-heading`" class="mb-4 text-center display-3 uppercase">
          {{ heading }}
        </h2>
        <div
          v-style:py="{ sm: '3rem', md: '6rem' }"
          class="relative grid gap-y-2 md:cols-2 md:gap-y-4"
          style="column-gap: 1px"
        >
          <span class="absolute left-1/2 h-full bg-grey-50 -translate-x-1/2" style="width: 1px" />
          <span class="absolute top-0 h-3 w-3 rounded-full bg-black" style="left: calc(50% - 0.375rem)" />
          <span class="absolute bottom-0 h-3 w-3 rounded-full bg-black" style="left: calc(50% - 0.375rem)" />
          <template v-for="(block, j) in blocks" :key="j">
            <div
              v-if="block.video"
              v-motion-fade-up-visible
              v-style:top="{ md: block.top }"
              class="h-fit w-full overflow-hidden md:relative"
              :class="{
                'md:col-span-2 md:justify-center': block.span === 2,
                'md:justify-end': block.span !== 2 && j % 2 === 0,
              }"
              style="padding-top: 56.25%"
            >
              <base-youtube :id="block.video.id" class="absolute-0 full" controls />
            </div>
            <div
              v-if="block.img"
              v-motion-fade-up-visible
              v-style:top="{ md: block.top }"
              class="h-fit w-full flex md:relative"
              :class="{
                'md:col-span-2 md:justify-center': block.span === 2,
                'md:justify-end': block.span !== 2 && j % 2 === 0,
              }"
            >
              <base-picture
                v-style:w="{ lg: block.width }"
                v-bind="block.img"
                class="w-full"
              />
            </div>
            <div
              v-if="block.paragraph"
              v-motion-fade-up-visible
              v-style:top="{ md: block.top }"
              class="w-full flex md:relative"
              :class="{
                'md:col-span-2 md:justify-center': block.span === 2,
                'md:justify-end': block.span !== 2 && j % 2 === 0,
              }"
            >
              <p
                v-style:w="{ lg: block.width }"
                class="h-fit bg-white p-5 md:p-10 <md:text-sm"
              >
                <base-interpolator :content="block.paragraph">
                  <template #b="{ args: [message, classes] }">
                    <span class="fw-bold" :class="classes">
                      {{ message }}
                    </span>
                  </template>
                  <template #i="{ args: [message] }">
                    <span class="italic">
                      {{ message }}
                    </span>
                  </template>
                  <template #sup="{ args: [message] }">
                    <sup>{{ message }}</sup>
                  </template>
                  <template #heading="{ args: [message] }">
                    <p v-html="message" class="text-xl fw-bold lh-none font-secondary uppercase lg:text-2xl" />
                  </template>
                  <template #link="{ args: [message, to] }">
                    <vf-link class="fw-bold" :to>
                      {{ message }}
                    </vf-link>
                  </template>
                </base-interpolator>
              </p>
            </div>
            <div v-if="block.empty" class="<md:hidden" />
          </template>
        </div>
      </section>
    </div>
  </main>
</template>

<script lang="ts" setup>
const { data, error } = await useApi().internal.getStaticPage('since-66')
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { bg, hero, years } = data.value

const header = useHeaderStore()

const currentSectionId = ref<string>(years[0].id)
const listItemRefs = ref<HTMLElement[]>([])
const navRef = ref<HTMLElement>()
const sectionsRefs = ref<HTMLElement[]>([])

const handleNavScroll = (id: string) => {
  const index = years.findIndex((year) => year.id === id)
  const listItem = listItemRefs.value[index]
  const nav = navRef.value!
  const { bottom: listItemBottom, top: listItemTop } = listItem.getBoundingClientRect()
  const { bottom: navBottom, top: navTop } = nav.getBoundingClientRect()
  const offset = 24
  const isOutOfViewAbove = listItemTop < navTop + offset
  const isOutOfViewBelow = listItemBottom > navBottom - offset

  if (isOutOfViewAbove || isOutOfViewBelow) {
    nav.scrollTo({
      top: listItem.offsetTop - nav.clientHeight / 2 + listItem.clientHeight / 2 - offset,
      behavior: 'smooth'
    })
  }
}

useIntersectionObserver(
  sectionsRefs,
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const id = entry.target.getAttribute('id') as string
        currentSectionId.value = id
        handleNavScroll(id)
      }
    })
  },
  { rootMargin: '-50% 0% -50% 0%', threshold: 0 }
)
</script>
